<script setup>
import { ref, onMounted } from 'vue';

const showGradient = ref(false);

onMounted(() => {
  // Small delay to ensure the browser has computed the layout
  // before we fade in the heavy gradient
  setTimeout(() => {
    showGradient.value = true;
  }, 100);
});
</script>

<template>
  <div class="container">
    <div 
      class="gradient-overlay"
      :class="{ 'is-visible': showGradient }"
    ></div>

    <div class="vertical-line first"></div>
    <div class="vertical-line second"></div>
    <div class="horizontal-line top"></div>
    <div class="horizontal-line bottom"></div>

    <div class="image-container">
      <img src="@/assets/homePreview.webp" alt="Centered Image" width="800" height="1603" />
    </div>

    <div class="text-customizable-preferences">
      <p class="font-light">Customizable Preferences</p>
    </div>

    <div class="text-privacy-focused">
      <p class="font-light">Privacy-Focused</p>
    </div>

    <div class="text-password-strength">
      <p class="font-light">Password Strength Indicator</p>
    </div>
  </div>
</template>

<style>
@import url('https://fonts.googleapis.com/css2?family=Albert+Sans:wght@300;400;500;700&display=swap');
</style>

<style scoped>
.font-light {
  font-family: 'Albert Sans', sans-serif;
  font-size: 22px; 
  font-weight: 300; 
  color: rgba(255, 255, 255, 0.7); 
  line-height: 33px;
}

/* --- DESKTOP STYLES --- */
.container {
  position: relative;
  width: 100vw;
  max-width: 1200px;
  height: 100vh;
  margin: 0 auto;
  background-color: #161616;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(3, 1fr);
  place-items: center;
}

.vertical-line, .horizontal-line { position: absolute; background-color: rgba(255, 255, 255, 0.15); }
.vertical-line { width: 1px; top: 20%; bottom: 20%; }
.vertical-line.first { left: 33.33%; }
.vertical-line.second { left: 66.66%; }

.horizontal-line { height: 1px; left: 0; right: 0; }
.horizontal-line.top { top: 33.33%; }
.horizontal-line.bottom { top: 66.66%; }

/* --- GRADIENT FIXES --- */
.gradient-overlay {
  grid-column: 2; grid-row: 2;
  background: linear-gradient(65deg, rgba(170, 47, 190, 0.5) 0%, rgba(25, 43, 156, 0.5) 100%);
  filter: blur(225px); 
  transform: rotate(65deg) translateZ(0); /* translateZ enables hardware acceleration */
  z-index: 0; 
  pointer-events: none;
  
  /* Initial State: Invisible */
  opacity: 0; 
  /* Smooth Fade In Transition */
  transition: opacity 0.8s ease-out; 
  will-change: opacity, transform;
}

/* The state when loaded */
.gradient-overlay.is-visible {
  opacity: 1;
}

.image-container {
  grid-column: 2; grid-row: 2; z-index: 1;
  width: 90%; max-width: 400px;
  filter: drop-shadow(0 0 20px rgba(170, 47, 190, 0.3));
}
.image-container img { max-width: 100%; height: auto; }

.text-customizable-preferences, .text-privacy-focused, .text-password-strength {
  position: absolute; z-index: 2;
  font-family: 'Albert Sans', sans-serif; font-size: 20px; font-weight: 400; color: rgba(255, 255, 255, 0.7); line-height: 1.6;
}
.text-customizable-preferences { top: 30%; left: 0%; transform: translateY(-50%); }
.text-privacy-focused { top: 28%; right: 18%; transform: translateY(-50%); }
.text-password-strength { bottom: 40%; right: 8.5%; transform: translateY(50%); }


/* --- MOBILE STYLES --- */
@media (max-width: 767px) {
  .container {
    display: block;
    height: auto;
    min-height: 60vh;
    padding-top: 40px;
  }

  .image-container {
    grid-column: 1;
    grid-row: 1; 
    width: 50%;
    margin: 25px auto 0 auto; 
    height: auto;
  }

  .vertical-line { height: 80%; top: 10%; bottom: 10%; }
  .container .vertical-line.first { left: 8px; right: auto; transform: none; }
  .container .vertical-line.second { left: auto; right: 8px; }

  .text-customizable-preferences .font-light,
  .text-privacy-focused .font-light,
  .text-password-strength .font-light { font-size: 12px; line-height: 16px; }

  .text-password-strength { width: 80px; text-align: right; right: 20px; }
  
  .text-customizable-preferences {
    width: 100px; text-align: left; left: 5%;
    top: 41.33%; transform: translateY(-50%);
  }

  .text-privacy-focused { width: 90px; text-align: right; right: 20px; }
}
</style>